import clsx from 'clsx';
// Lupa inline (FaIcon) y no el pack de Font Awesome: el Rail y la Navbar son eager y
// este trigger era el único import de @fortawesome en el camino de arranque.
import { FaIcon } from '@/components/FaIcon/FaIcon';
import { useAuth } from '@/contexts';
import { FunctionSearchIcon } from './FunctionSearchIcon';
import { openFunctionSearch } from './events';
import { TOUCH_HIT_AREA } from '@/utils/touchTarget';
// Directo al archivo y no al índice del Rail: el Rail importa este trigger.
import { RailTooltip } from '@/components/Rail/components/RailTooltip';

type Props = { variant: 'rail'; collapsed: boolean } | { variant: 'topbar'; collapsed?: never };

/** El atajo se escribe como lo tiene el teclado que lo va a usar, no como lo tiene el nuestro. */
const shortcutLabel = (): string =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '') ? '⌘ K' : 'Ctrl K';

export const FunctionSearchTrigger = (props: Props) => {
  const { session } = useAuth();

  // El buscador es del DUEÑO: el staff no lo tiene (decisión de producto, 14/8).
  // El gate vive acá y en FunctionSearchDialog — los call sites no necesitan saberlo.
  if (session?.is_staff) return null;

  if (props.variant === 'topbar') {
    return (
      <button
        type="button"
        onClick={() => openFunctionSearch('topbar')}
        aria-label="Buscar funciones"
        title="Buscar funciones"
        className={clsx(
          // Botón con etiqueta: en mobile es lo único con texto, así que deja de leerse
          // como un ícono más al lado de la campana (Isa, 18/8). Control neutro, blanco
          // con línea fina y radio 8 (como cualquier campo), sin relleno de marca. La
          // altura (h-9) acompaña a campana y burger para que la fila quede pareja.
          // Ancho ~96px: entra sin overflow hasta 320px. 36 px a la vista, 44 px al toque.
          'relative flex h-9 items-center gap-2 rounded-lg border border-frost-hair-stronger bg-frost-card pl-2.5 pr-3 text-frost-ink-soft',
          TOUCH_HIT_AREA,
          'transition-[background-color,color] duration-200 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]',
          'hover:text-frost-ink',
          'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-frost-brand/30'
        )}
      >
        <FunctionSearchIcon className="h-[18px] w-[18px] shrink-0" strokeWidth={2} aria-hidden />
        <span className="text-fs-13-5 font-semibold leading-none">Buscar</span>
      </button>
    );
  }

  const railLabel = `Buscar funciones (${shortcutLabel()})`;

  // Blanco con línea fina y radio 8, como cualquier campo. Contraído, el nombre sale en
  // el globo del riel en vez del `title` nativo.
  return (
    <RailTooltip label={railLabel} enabled={props.collapsed}>
      <button
        type="button"
        onClick={() => openFunctionSearch('rail')}
        aria-label="Buscar funciones"
        className={clsx(
          'flex shrink-0 items-center rounded-lg bg-frost-card text-frost-ink-mid shadow-[inset_0_0_0_1px_rgba(var(--f-ink-rgb),0.14)]',
          'transition-[background,color,box-shadow] duration-200 hover:text-frost-ink hover:shadow-[inset_0_0_0_1px_rgba(var(--f-ink-rgb),0.24)]',
          'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-frost-brand/30',
          props.collapsed ? 'mx-auto h-9 w-9 justify-center px-0' : 'h-[38px] w-full gap-2.5 pl-3.5 pr-1.5'
        )}
      >
        {/* Lupa llena de 14px, como los íconos del riel (la de trazo queda para la barra
            del celular y el campo del panel). */}
        <FaIcon name="magnifying_glass" className="w-[1em] shrink-0 text-fs-14" />
        {!props.collapsed && (
          <>
            <span className="min-w-0 flex-1 whitespace-nowrap text-left text-fs-13-5 font-medium">
              Buscar funciones
            </span>
            <kbd className="rounded-frost-sm bg-frost-card px-1.5 py-[3px] font-montserrat text-fs-10 font-semibold text-frost-ink-mid shadow-[inset_0_0_0_1px_rgba(var(--f-ink-rgb),0.16)]">
              {shortcutLabel()}
            </kbd>
          </>
        )}
      </button>
    </RailTooltip>
  );
};
